import { Gender, QuestionnaireVariant } from '@aldebaran/prisma';

/**
 * MMG (Maisons Médicales de Garde) variants — on-call / walk-in care. `mmg` is
 * the patient-phone (QR code) flow; `mmg_borne` is the waiting-room kiosk. Both
 * get the reframed "check-in / join the queue" patient UX (ALDO-2540).
 */
export const isMmgVariant = (
  variant: QuestionnaireVariant | string | null | undefined,
): boolean => variant === 'mmg' || variant === 'mmg_borne';

export type MmgCheckInCopy = {
  /** Heading — "Vous êtes bien enregistré / enregistrée" */
  title: string;
  /** "Vos réponses ont été transmises au médecin de garde." – null when the
   * title already says it */
  transmitted: string | null;
  /** Queue/waiting instruction, gendered. */
  queue: string;
};

/**
 * The reframed offboarding copy shown to a patient who has checked in for an
 * MMG consultation, gendered to the patient's sex (neutral both-forms shape when
 * unknown). No checkmark in the copy — the check is shown graphically next to
 * the professional's photo (ALDO-2540).
 *
 * A kiosk continuation (ALDO-2759) checked the patient in at the kiosk already,
 * so its phone questionnaire ends on the answers being sent instead.
 */
export const getMmgCheckInCopy = (
  sex: Gender | null | undefined,
  isKioskContinuation = false,
): MmgCheckInCopy => {
  const registered =
    sex === 'MALE'
      ? 'enregistré'
      : sex === 'FEMALE'
      ? 'enregistrée'
      : 'enregistré(e)';
  const called =
    sex === 'MALE' ? 'appelé' : sex === 'FEMALE' ? 'appelée' : 'appelé(e)';

  return {
    title: isKioskContinuation
      ? 'Merci, vos réponses ont été transmises'
      : `Vous êtes bien ${registered}`,
    transmitted: isKioskContinuation
      ? null
      : 'Vos réponses ont été transmises au médecin de garde.',
    queue: `Vous serez ${called} selon votre ordre d'arrivée – merci de patienter en salle d'attente.`,
  };
};
